import createSagaMiddleware from 'redux-saga';
import combineSectionReducers from 'combine-section-reducers';
import {
  GlobalEventDispatcher,
  GlobalPut,
  integrateReduxDevtoolsExtension,
  NgRefreshStore,
  PS20Store,
} from '@ps20-frontend/shop-integration-redux';
import {
  gesamtbeitragMitRabattReducer,
  gesamtbeitragReducerForApp,
  jahresbeitragReducer,
  rabattstufeReducer,
} from '@ps20-frontend/reducers';
import { App } from '@ps20-frontend/frontend-models';
import { configureStore, Dispatch, Middleware, Tuple, UnknownAction } from '@reduxjs/toolkit';
import { IAppState } from './appState';
import { rootSaga } from './sagas/rootSaga';
import { risikoortReducer } from './reducers/risikoort.reducer';
import { versicherungsnehmerReducer } from './reducers/versicherungsnehmer.reducer';
import { versObjektReducer } from './reducers/versObjekt.reducer';
import { vorschaedenReducer } from './reducers/vorschaeden.reducer';
import { rootReducer } from './reducers/rootReducer.reducer';
import { vhvProduktReducer } from './reducers/produkt.reducer';
import { INITIAL_STATE } from './initialState';
import { adressvorschlagReducer } from './reducers/adressvorschlag.reducer';
import { vhvVertragsoptionReducer } from './reducers/vertragsoptionen.reducer';
import { vhvTaasInfoReducer } from './reducers/vhvTaasInfo.reducer';
import { wertsachensummenReducer } from './reducers/wertsachensummen.reducer';
import { unterversicherungsVerzichtReducer } from './reducers/unterversicherungsverzicht.reducer';
import { vorbelegungenReducer } from './reducers/vorbelegungen.reducer';
import { gisDatenReducer } from './reducers/gisDaten.reducer';
import { plzFaktorenReducer } from './reducers/plzFaktoren.reducer';
import { transientStateReducer } from './reducers/transient-state.reducer';
import { elementarSelbstbehalt } from './reducers/elementar-selbstbehalt.reducer';
import { extremwetterschutzSelbstbeteiligungReducer } from './reducers/extremwetterschutzSelbstbeteiligung.reducer';
import { tragerisikoReducer } from './reducers/tragerisiko.reducer';
import { antragsnummerReducer } from './reducers/antragsnummer.reducer';
import { versicherungssummeFahrradReducer } from './reducers/versicherungssummeFahrrad.reducer';
import { entschaedigungsgrenzeRadReducer } from './reducers/entschaedigungsgrenzeRad.reducer';
import { versicherungssummenReducer } from './reducers/versicherungssummen.reducer';
import { selectedWertsachensummeReducer } from './reducers/selectedWertsachensummen.reducer';
import { hoechsteEinzelwerteReducer } from './reducers/hoechsteEinzelwerte.reducer';

export const reducers = combineSectionReducers({
  version: (version: number = null) => version,
  adressvorschlag: adressvorschlagReducer,
  antragsnummer: antragsnummerReducer,
  elementarSelbstbehalt,
  extremwetterschutzSelbstbeteiligung: extremwetterschutzSelbstbeteiligungReducer,
  gesamtbeitrag: gesamtbeitragReducerForApp(App.VHV_V2),
  gesamtbeitragMitRabatt: gesamtbeitragMitRabattReducer,
  gisDaten: gisDatenReducer,
  plzFaktoren: plzFaktorenReducer,
  hoechsteEinzelwerte: hoechsteEinzelwerteReducer,
  jahresbeitrag: jahresbeitragReducer,
  produkt: vhvProduktReducer,
  rabattstufe: rabattstufeReducer,
  risikoort: risikoortReducer,
  selectedWertsachensumme: selectedWertsachensummeReducer,
  taasInfo: vhvTaasInfoReducer,
  tragerisiko: tragerisikoReducer,
  transientState: transientStateReducer,
  unterversicherungsverzicht: unterversicherungsVerzichtReducer,
  versObjekt: versObjektReducer,
  versicherungsnehmer: versicherungsnehmerReducer,
  versicherungssummeFahrrad: versicherungssummeFahrradReducer,
  entschaedigungsgrenzeRad: entschaedigungsgrenzeRadReducer,
  versicherungssummen: versicherungssummenReducer,
  vertragsoptionen: vhvVertragsoptionReducer,
  vorbelegungen: vorbelegungenReducer,
  vorschaeden: vorschaedenReducer,
  wertsachensummen: wertsachensummenReducer,
});

export function vhvStoreFactory(storeIndex: number): PS20Store<IAppState> {
  const ngRefreshStore = new NgRefreshStore<IAppState>(App.VHV_V2);
  const sagaMiddleware = createSagaMiddleware();

  const store = configureStore({
    reducer: rootReducer(reducers),
    devTools: integrateReduxDevtoolsExtension(`vhv-angebot-v2-gui-${storeIndex}`),

    preloadedState: INITIAL_STATE,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware().concat(sagaMiddleware, ngRefreshStore.ngRefreshMiddleware) as unknown as Tuple<ReadonlyArray<Middleware<Record<string, never>, IAppState, Dispatch<UnknownAction>>>>,
  });

  sagaMiddleware.run(rootSaga);
  ngRefreshStore.setDelegate(store);
  return ngRefreshStore;
}

GlobalPut.getInstance().globalEventDispatcherFunction = () => {
  return (window as unknown as Record<string, unknown>)['globalEventDispatcher'] as GlobalEventDispatcher;
};
